import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Plus, Trash2, Volume2, Activity } from 'lucide-react';
import FirstVisitHint from './FirstVisitHint';

const Timeline = ({ 
  nodes, 
  setNodes, 
  duration, 
  currentTime, 
  setCurrentTime, 
  brainwaveRanges,
  isPlaying,
  currentBrainwave,
  beatType,
  baseFrequency,
  getCurrentFrequency,
  audioContextRef,
  oscillatorsRef,
  isPaused,
  pauseAnimationFrame,
  resumeAnimationFrame,
  updateStartTimeForManualSeek,
  volumeNodes = [],
  setVolumeNodes,
  gainNodeRef,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [draggedNode, setDraggedNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDraggingTimeline, setIsDraggingTimeline] = useState(false);
  const [timelineDragStarted, setTimelineDragStarted] = useState(false);
  const [hasActuallyDragged, setHasActuallyDragged] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [activeLayer, setActiveLayer] = useState('frequency'); // 'frequency' | 'volume'
  const [draggedVolumeNode, setDraggedVolumeNode] = useState(null);
  const timelineRef = useRef(null);
  // Tracks the touch identifier of the in-progress drag (Android fires
  // touchcancel for ANY touch on the document — pinch-zoom, second-finger
  // taps, scroll disambiguation — and we must only treat our specific drag
  // touch's cancellation as drag-end, otherwise normal touch dragging gets
  // interrupted mid-gesture.
  const dragTouchIdRef = useRef(null);
  // Long-press-to-delete on touch. We schedule a 500ms timer on touchstart;
  // any meaningful finger movement OR touchend cancels it. If it elapses,
  // we fire deleteNode with a short haptic buzz. This replaces the
  // always-visible × button on mobile, which was eating drag touches.
  const longPressTimerRef = useRef(null);
  // After a long-press fires deleteNode, the browser still synthesises a
  // `click` event when the finger lifts. That click bubbles into the
  // timeline container, hits handleTimelineClick, and adds a fresh node at
  // the same tap position — the exact bug a user just reported on Android.
  // We set this ref the instant we delete, and handleTimelineClick ignores
  // the very next synthetic click while the ref is true.
  const suppressNextClickRef = useRef(false);
  const LONG_PRESS_MS = 500;
  const LONG_PRESS_MOVE_THRESHOLD = 8; // pixels — anything beyond this is a drag, not a press

  // Detect once on mount whether this device has a real mouse / trackpad
  // (i.e., supports CSS :hover meaningfully). True on laptops + desktops,
  // false on phones + most tablets. Used to gate the always-rendered
  // delete-× button — touch-only devices skip it entirely and use
  // long-press-to-delete instead. Window-size independent, so split-screen
  // MacBooks still get the × that viewport-based `md:` gating would hide.
  const [hasFineHover, setHasFineHover] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    try {
      return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    } catch {
      return true;
    }
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    let mq;
    try {
      mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    } catch {
      return;
    }
    const onChange = (e) => setHasFineHover(e.matches);
    // Older Safari uses addListener; modern uses addEventListener
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  const TIMELINE_HEIGHT = 400;
  const TIMELINE_PADDING = 40;
  const NODE_SIZE = 12;
  const VOL_NODE_SIZE = 10;

  // Convert frequency to Y position
  const frequencyToY = (frequency) => {
    const maxFreq = 100;
    const minFreq = 0.5;
    const logFreq = Math.log(frequency);
    const logMax = Math.log(maxFreq);
    const logMin = Math.log(minFreq);
    const normalizedY = (logFreq - logMin) / (logMax - logMin);
    return TIMELINE_HEIGHT - TIMELINE_PADDING - (normalizedY * (TIMELINE_HEIGHT - 2 * TIMELINE_PADDING));
  };

  // Convert Y position to frequency
  const yToFrequency = (y) => {
    const maxFreq = 100;
    const minFreq = 0.5;
    const logMax = Math.log(maxFreq);
    const logMin = Math.log(minFreq);
    const normalizedY = (TIMELINE_HEIGHT - TIMELINE_PADDING - y) / (TIMELINE_HEIGHT - 2 * TIMELINE_PADDING);
    const logFreq = logMin + (normalizedY * (logMax - logMin));
    return Math.max(minFreq, Math.min(maxFreq, Math.exp(logFreq)));
  };

  // Convert volume (0-1) to Y position
  const volumeToY = (vol) => {
    const clamped = Math.max(0, Math.min(1, vol));
    return TIMELINE_HEIGHT - TIMELINE_PADDING - (clamped * (TIMELINE_HEIGHT - 2 * TIMELINE_PADDING));
  };

  // Convert Y position to volume (0-1)
  const yToVolume = (y) => {
    const normalized = (TIMELINE_HEIGHT - TIMELINE_PADDING - y) / (TIMELINE_HEIGHT - 2 * TIMELINE_PADDING);
    return Math.max(0, Math.min(1, normalized));
  };

  // Convert time to X position
  const timeToX = (time) => {
    if (!timelineRef.current || !timelineRef.current.clientWidth) return TIMELINE_PADDING;
    return (time / duration) * (timelineRef.current.clientWidth - 2 * TIMELINE_PADDING) + TIMELINE_PADDING;
  };

  // Convert X position to time
  const xToTime = (x) => {
    if (!timelineRef.current || !timelineRef.current.clientWidth) return 0;
    const timelineWidth = timelineRef.current.clientWidth - 2 * TIMELINE_PADDING;
    return Math.max(0, Math.min(duration, ((x - TIMELINE_PADDING) / timelineWidth) * duration));
  };

  // Get brainwave color for frequency
  const getBrainwaveColor = (frequency) => {
    for (const [type, range] of Object.entries(brainwaveRanges)) {
      if (frequency >= range.min && frequency <= range.max) {
        return range.color;
      }
    }
    return '#9CA3AF';
  };

  // Get brainwave type for frequency
  const getBrainwaveType = (frequency) => {
    for (const [type, range] of Object.entries(brainwaveRanges)) {
      if (frequency >= range.min && frequency <= range.max) {
        return { type, ...range };
      }
    }
    return { type: 'custom', color: '#9CA3AF', name: 'Custom' };
  };

  // Handle mouse/touch down on timeline indicator
  const handleTimelineIndicatorMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingTimeline(true);
    setTimelineDragStarted(true);
    
    if (pauseAnimationFrame) {
      pauseAnimationFrame();
    }
    
    const rect = timelineRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    const newTime = xToTime(x);
    setCurrentTime(newTime);
  };

  const handleTimelineIndicatorTouchStart = (e) => {
    handleTimelineIndicatorMouseDown(e);
  };

  // Handle mouse down on frequency node
  const handleMouseDown = (e, nodeId) => {
    e.preventDefault();
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    setIsDragging(true);
    setDraggedNode(nodeId);
    setDraggedVolumeNode(null);
    setHasActuallyDragged(false);
    
    const rect = timelineRef.current.getBoundingClientRect();
    const nodeX = timeToX(node.time);
    const nodeY = frequencyToY(node.frequency);
    
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setDragOffset({
      x: e.clientX - rect.left - nodeX,
      y: e.clientY - rect.top - nodeY
    });
  };

  // Handle touch start on frequency node
  const handleTouchStart = (e, nodeId) => {
    e.preventDefault();
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    const touch = e.touches[0];
    // Record the drag-touch identifier so the global touchcancel handler
    // can distinguish "our touch was cancelled" from "some other touch on
    // the page was cancelled". See `dragTouchIdRef` declaration for why.
    dragTouchIdRef.current = touch.identifier;
    setIsDragging(true);
    setDraggedNode(nodeId);
    setDraggedVolumeNode(null);
    setHasActuallyDragged(false);
    
    const rect = timelineRef.current.getBoundingClientRect();
    const nodeX = timeToX(node.time);
    const nodeY = frequencyToY(node.frequency);
    
    setDragStartPos({ x: touch.clientX, y: touch.clientY });
    setDragOffset({
      x: touch.clientX - rect.left - nodeX,
      y: touch.clientY - rect.top - nodeY
    });

    // Schedule long-press-to-delete. Cancelled on any meaningful move
    // (handleMouseMove) or on touchend (handleMouseUp).
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      // Haptic confirmation if the device supports it. Best-effort; some
      // browsers (iOS Safari) silently no-op.
      try { if (navigator.vibrate) navigator.vibrate(40); } catch { /* ignore */ }
      // Mute the synthetic events that Android fires when the finger lifts
      // after a long-press: not just the synthesised `click`, but ALSO
      // `pointerup` / `mouseup` / `tap` chains depending on browser. We
      // install a capture-phase click swallower at the document level (the
      // most aggressive available) and tear it down 700ms later — enough
      // time for even slow Android devices to deliver the synthetic chain.
      suppressNextClickRef.current = true;
      const swallow = (ev) => {
        // Stop propagation + prevent default on ANY click within the window.
        // We only need ONE — the suppress flag is consumed on the first hit,
        // but the listener stays installed for the full window so chained
        // synthetic events (some Android versions fire multiple) all die.
        ev.stopPropagation();
        ev.preventDefault();
      };
      // useCapture: true — catches the event before it reaches the timeline
      // canvas onClick. `once: false` because Android may fire more than one.
      document.addEventListener('click', swallow, { capture: true });
      setTimeout(() => {
        suppressNextClickRef.current = false;
        document.removeEventListener('click', swallow, { capture: true });
      }, 700);
      deleteNode(nodeId);
    }, LONG_PRESS_MS);
  };

  // Handle mouse down on volume node
  const handleVolumeNodeMouseDown = (e, nodeId) => {
    e.preventDefault();
    e.stopPropagation();
    const node = volumeNodes.find(n => n.id === nodeId);
    if (!node) return;

    setIsDragging(true);
    setDraggedVolumeNode(nodeId);
    setDraggedNode(null);
    setHasActuallyDragged(false);
    
    const rect = timelineRef.current.getBoundingClientRect();
    const nodeX = timeToX(node.time);
    const nodeY = volumeToY(node.volume);
    
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setDragOffset({
      x: e.clientX - rect.left - nodeX,
      y: e.clientY - rect.top - nodeY
    });
  };

  // Handle mouse/touch move
  const handleMouseMove = (e) => {
    // Defensive guard: if neither flag is set there's nothing to move. The
    // global listener is only attached while one of these is true, but the
    // closure captures stale state under some Android scroll-cancel paths,
    // so we re-check from props/state via ref-style closure on every call.
    if (!isDragging && !isDraggingTimeline) return;
    // Equally defensive: a `draggedNode` of null means we somehow entered
    // drag mode without a target — bail rather than mutating arbitrary nodes.
    if (isDragging && !draggedNode && !draggedVolumeNode) return;

    // Cancel pending long-press-to-delete the moment the finger moves
    // beyond a small threshold — meaningful movement means the user
    // is dragging, not pressing-and-holding.
    if (longPressTimerRef.current) {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = clientX - dragStartPos.x;
      const dy = clientY - dragStartPos.y;
      if (dx * dx + dy * dy > LONG_PRESS_MOVE_THRESHOLD * LONG_PRESS_MOVE_THRESHOLD) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }

    if (isDraggingTimeline) {
      const rect = timelineRef.current.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const x = clientX - rect.left;
      const newTime = xToTime(x);
      setCurrentTime(newTime);
      
      if (isPlaying && audioContextRef?.current && updateStartTimeForManualSeek) {
        updateStartTimeForManualSeek(newTime);
      }
      
      if (isPlaying && audioContextRef?.current && oscillatorsRef?.current.length > 0) {
        const currentFreq = getCurrentFrequency ? getCurrentFrequency(newTime) : 10;
        try {
          if (beatType === 'binaural') {
            if (oscillatorsRef.current[0]) oscillatorsRef.current[0].frequency.value = baseFrequency;
            if (oscillatorsRef.current[1]) oscillatorsRef.current[1].frequency.value = baseFrequency + currentFreq;
          } else if (beatType === 'monaural') {
            if (oscillatorsRef.current[0]) oscillatorsRef.current[0].frequency.value = baseFrequency;
            if (oscillatorsRef.current[1]) oscillatorsRef.current[1].frequency.value = baseFrequency + currentFreq;
          } else if (beatType === 'isochronic') {
            if (oscillatorsRef.current[0]) oscillatorsRef.current[0].frequency.value = baseFrequency;
            if (oscillatorsRef.current[1]) oscillatorsRef.current[1].frequency.value = currentFreq;
          } else if (beatType === 'heartbeat') {
            if (oscillatorsRef.current[0]) oscillatorsRef.current[0].frequency.value = 60;
            if (oscillatorsRef.current[1]) oscillatorsRef.current[1].frequency.value = currentFreq / 60;
          } else if (beatType === 'trypnaural') {
            if (oscillatorsRef.current[0]) oscillatorsRef.current[0].frequency.value = baseFrequency;
            if (oscillatorsRef.current[1]) oscillatorsRef.current[1].frequency.value = baseFrequency + currentFreq;
            if (oscillatorsRef.current[2]) oscillatorsRef.current[2].frequency.value = baseFrequency + (currentFreq * 2);
          }
        } catch (error) {
          console.warn('Error updating oscillator frequencies:', error);
        }
      }
      return;
    }
    
    if (!isDragging) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const DRAG_THRESHOLD = 5;
    const distanceMoved = Math.sqrt(
      Math.pow(clientX - dragStartPos.x, 2) + 
      Math.pow(clientY - dragStartPos.y, 2)
    );
    
    if (distanceMoved > DRAG_THRESHOLD && !hasActuallyDragged) {
      setHasActuallyDragged(true);
    }
    
    const x = clientX - rect.left - dragOffset.x;
    const y = clientY - rect.top - dragOffset.y;
    const newTime = xToTime(x);

    if (draggedVolumeNode && setVolumeNodes) {
      const newVolume = yToVolume(y);
      setVolumeNodes(volumeNodes.map(node => 
        node.id === draggedVolumeNode 
          ? { ...node, time: newTime, volume: newVolume }
          : node
      ));
      // Live-update gain during drag
      if (gainNodeRef?.current && audioContextRef?.current) {
        gainNodeRef.current.gain.value = newVolume;
      }
    } else if (draggedNode) {
      const newFrequency = yToFrequency(y);
      setNodes(nodes.map(node => 
        node.id === draggedNode 
          ? { ...node, time: newTime, frequency: newFrequency }
          : node
      ));
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    if (isDraggingTimeline) {
      setIsDraggingTimeline(false);
      if (resumeAnimationFrame) {
        resumeAnimationFrame();
      }
      setTimeout(() => {
        setTimelineDragStarted(false);
      }, 50);
    }
    
    if (isDragging) {
      setIsDragging(false);
      setDraggedNode(null);
      setDraggedVolumeNode(null);
      setDragOffset({ x: 0, y: 0 });
      // Clear the drag-touch identifier so the next drag starts clean.
      dragTouchIdRef.current = null;
      // Cancel any pending long-press — if the touch ended before the
      // 500ms timer, this was a regular tap or short drag, not a delete.
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      setTimeout(() => {
        setHasActuallyDragged(false);
      }, 100);
    }
  };

  // Handle timeline click to add node
  const handleTimelineClick = (e) => {
    if (isDragging || isDraggingTimeline || timelineDragStarted) return;
    // Swallow the synthetic click that fires right after a long-press
    // delete on touch devices. Without this, Android long-press deletes a
    // node then immediately re-creates an identical one at the same spot.
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }
    if (e.target.closest('.timeline-indicator')) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const newTime = xToTime(x);

    if (activeLayer === 'volume' && setVolumeNodes) {
      const newVolume = yToVolume(y);
      const newNode = {
        id: `v_${Date.now()}`,
        time: newTime,
        volume: newVolume
      };
      setVolumeNodes([...volumeNodes, newNode]);
    } else {
      const newFrequency = yToFrequency(y);
      const newNode = {
        id: Date.now().toString(),
        time: newTime,
        frequency: newFrequency
      };
      setNodes([...nodes, newNode]);
    }
  };

  // Delete node
  const deleteNode = (nodeId) => {
    setNodes(nodes.filter(node => node.id !== nodeId));
    // Critical: also tear down the in-progress drag. handleMouseMove is
    // attached as a global touchmove/mousemove listener with a STALE
    // `nodes` closure (captured when isDragging flipped to true). If we
    // only setNodes(filter), the next touchmove call uses the stale
    // closure + the still-set `draggedNode` to re-create the deleted
    // node via `nodes.map(...)` — that's the exact "delete then reappear"
    // bug the user reported on Android. Clearing isDragging triggers the
    // listener-attach useEffect to re-run with the fresh closure, AND
    // the early `if (!isDragging) return;` guard in handleMouseMove now
    // bails before the resurrection map runs.
    setIsDragging(false);
    setDraggedNode(null);
    setDraggedVolumeNode(null);
    dragTouchIdRef.current = null;
  };

  const deleteVolumeNode = (nodeId) => {
    if (setVolumeNodes && volumeNodes.length > 2) {
      setVolumeNodes(volumeNodes.filter(node => node.id !== nodeId));
      // Same stale-closure defence as deleteNode — tear down any in-progress
      // drag of the volume node we just deleted so handleMouseMove (with a
      // pre-delete `volumeNodes` closure) doesn't resurrect it on the next
      // touchmove. See deleteNode comment above for full reasoning.
      setIsDragging(false);
      setDraggedNode(null);
      setDraggedVolumeNode(null);
      dragTouchIdRef.current = null;
    }
  };

  // Add event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalTouchMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();
    const handleGlobalTouchEnd = () => handleMouseUp();
    // Only react to touchcancel if our specific drag-touch is in the
    // cancelled list — otherwise unrelated touches (a second finger, the
    // browser disambiguating gestures elsewhere on the page) would tear
    // down an in-progress drag.
    const handleGlobalTouchCancel = (e) => {
      const ourId = dragTouchIdRef.current;
      if (ourId == null) { handleMouseUp(); return; }
      let ourTouchCancelled = false;
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === ourId) {
          ourTouchCancelled = true;
          break;
        }
      }
      if (ourTouchCancelled) handleMouseUp();
    };
    
    if (isDragging || isDraggingTimeline) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchend', handleGlobalTouchEnd);
      document.addEventListener('touchcancel', handleGlobalTouchCancel);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
      document.removeEventListener('touchcancel', handleGlobalTouchCancel);
    };
  }, [isDragging, isDraggingTimeline, draggedNode, draggedVolumeNode, dragOffset]);

  // Generate frequency grid lines
  const generateFrequencyGrid = () => {
    const frequencies = [1, 2, 4, 8, 13, 30, 60, 100];
    return frequencies.map(freq => ({
      frequency: freq,
      y: frequencyToY(freq),
      label: `${freq} Hz`
    }));
  };

  // Generate volume grid lines
  const generateVolumeGrid = () => {
    return [0, 0.25, 0.5, 0.75, 1.0].map(vol => ({
      volume: vol,
      y: volumeToY(vol),
      label: `${Math.round(vol * 100)}%`
    }));
  };

  // Generate time grid lines
  const generateTimeGrid = () => {
    if (!duration || duration <= 0) return [];
    const lines = [];
    const totalMinutes = Math.ceil(duration / 60);
    const intervalMinutes = 5;
    const totalIntervals = Math.ceil(totalMinutes / intervalMinutes);
    
    for (let i = 0; i <= totalIntervals; i++) {
      const minutes = i * intervalMinutes;
      const time = minutes * 60;
      if (time <= duration) {
        lines.push({ time, x: timeToX(time), label: `${minutes}:00`, major: true });
      }
    }
    
    if (duration <= 600) {
      for (let minute = 1; minute < totalMinutes; minute++) {
        if (minute % 5 !== 0) {
          const time = minute * 60;
          if (time <= duration) {
            lines.push({ time, x: timeToX(time), label: `${minute}:00`, major: false });
          }
        }
      }
    }
    return lines.sort((a, b) => a.time - b.time);
  };

  // Generate smooth frequency curve path
  const generateCurvePath = () => {
    if (nodes.length < 2) return '';
    const sortedNodes = [...nodes].sort((a, b) => a.time - b.time);
    let path = '';
    for (let i = 0; i < sortedNodes.length; i++) {
      const node = sortedNodes[i];
      const x = timeToX(node.time);
      const y = frequencyToY(node.frequency);
      if (i === 0) {
        path += `M ${x} ${y}`;
      } else {
        const prevNode = sortedNodes[i - 1];
        const prevX = timeToX(prevNode.time);
        const prevY = frequencyToY(prevNode.frequency);
        const cpX1 = prevX + (x - prevX) * 0.5;
        const cpY1 = prevY;
        const cpX2 = prevX + (x - prevX) * 0.5;
        const cpY2 = y;
        path += ` C ${cpX1} ${cpY1}, ${cpX2} ${cpY2}, ${x} ${y}`;
      }
    }
    return path;
  };

  // Generate volume curve path
  const generateVolumeCurvePath = () => {
    if (!volumeNodes || volumeNodes.length < 2) return '';
    const sorted = [...volumeNodes].sort((a, b) => a.time - b.time);
    let path = '';
    for (let i = 0; i < sorted.length; i++) {
      const node = sorted[i];
      const x = timeToX(node.time);
      const y = volumeToY(node.volume);
      if (i === 0) {
        path += `M ${x} ${y}`;
      } else {
        const prev = sorted[i - 1];
        const prevX = timeToX(prev.time);
        const prevY = volumeToY(prev.volume);
        path += ` L ${prevX + (x - prevX) * 0.3} ${prevY} L ${prevX + (x - prevX) * 0.7} ${y} L ${x} ${y}`;
      }
    }
    return path;
  };

  const frequencyGrid = generateFrequencyGrid();
  const volumeGrid = generateVolumeGrid();
  const timeGrid = generateTimeGrid();
  const curvePath = generateCurvePath();
  const volumeCurvePath = generateVolumeCurvePath();
  const showVolumeLayer = volumeNodes && volumeNodes.length >= 2;

  return (
    <div className="space-y-4" data-testid="timeline-panel">
      {/* Touch-only "Hold to delete" hint — first-visit nudge, auto-fades */}
      <FirstVisitHint
        storageKey="neurowave_timeline_hold_hint"
        onlyTouch
        icon={<span aria-hidden className="text-base leading-none">⊕</span>}
        data-testid="timeline-hold-hint"
      >
        tap empty space to add a node ·{' '}
        <span className="font-medium">hold a node</span> to delete
      </FirstVisitHint>
      {/* Touch-only "drag the playhead" hint — separate storage key so each
          gesture is taught independently and dismissing one doesn't
          silence the other. */}
      <FirstVisitHint
        storageKey="neurowave_timeline_scrub_hint"
        onlyTouch
        icon={<span aria-hidden className="text-base leading-none">⇆</span>}
        data-testid="timeline-scrub-hint"
      >
        drag the <span className="font-medium text-red-300">red line</span> to scrub through the session
      </FirstVisitHint>
      {/* Timeline Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Layer Toggle */}
        <div className="flex bg-white/10 rounded-lg p-0.5 border border-white/20" data-testid="layer-toggle">
          <button
            onClick={() => setActiveLayer('frequency')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              activeLayer === 'frequency' ? 'bg-blue-600 text-white' : 'text-white/60 hover:text-white'
            }`}
            data-testid="layer-frequency-btn"
          >
            <Activity className="w-3.5 h-3.5" />
            Frequency
          </button>
          <button
            onClick={() => setActiveLayer('volume')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              activeLayer === 'volume' ? 'bg-amber-600 text-white' : 'text-white/60 hover:text-white'
            }`}
            data-testid="layer-volume-btn"
          >
            <Volume2 className="w-3.5 h-3.5" />
            Volume
          </button>
        </div>

        <Button
          onClick={() => {
            if (activeLayer === 'volume' && setVolumeNodes) {
              const newNode = {
                id: `v_${Date.now()}`,
                time: duration * 0.5,
                volume: 0.5
              };
              setVolumeNodes([...volumeNodes, newNode]);
            } else {
              const newNode = {
                id: Date.now().toString(),
                time: duration * 0.5,
                frequency: 10
              };
              setNodes([...nodes, newNode]);
            }
          }}
          className={activeLayer === 'volume' ? "bg-amber-600 hover:bg-amber-700" : "bg-green-600 hover:bg-green-700"}
          size="sm"
          data-testid="add-node-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add {activeLayer === 'volume' ? 'Volume' : 'Freq'} Node
        </Button>
        
        <div className="text-white text-sm">
          {activeLayer === 'frequency' ? `${nodes.length} freq nodes` : `${volumeNodes.length} vol nodes`}
          {' | '}
          {duration >= 3600 
            ? `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m` 
            : `${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')}`}
        </div>
      </div>

      {/* Main Timeline */}
      <div 
        ref={timelineRef}
        className="relative bg-black/30 rounded-lg cursor-crosshair"
        style={{ height: TIMELINE_HEIGHT }}
        onClick={handleTimelineClick}
        data-testid="timeline-canvas"
      >
        {/* Frequency Grid (always visible, dimmed when volume layer active) */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          {activeLayer === 'frequency' ? frequencyGrid.map((line, i) => (
            <g key={i}>
              <line x1={TIMELINE_PADDING} y1={line.y} x2="100%" y2={line.y} stroke="rgba(255, 255, 255, 0.1)" strokeWidth="1" />
              <text x={TIMELINE_PADDING - 5} y={line.y + 4} fill="rgba(255, 255, 255, 0.6)" fontSize="12" textAnchor="end">
                {line.label}
              </text>
            </g>
          )) : volumeGrid.map((line, i) => (
            <g key={i}>
              <line x1={TIMELINE_PADDING} y1={line.y} x2="100%" y2={line.y} stroke="rgba(251, 191, 36, 0.15)" strokeWidth="1" />
              <text x={TIMELINE_PADDING - 5} y={line.y + 4} fill="rgba(251, 191, 36, 0.6)" fontSize="12" textAnchor="end">
                {line.label}
              </text>
            </g>
          ))}
        </svg>

        {/* Time Grid */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          {timeGrid.map((line, i) => (
            <g key={i}>
              <line
                x1={line.x} y1={TIMELINE_PADDING} x2={line.x} y2={TIMELINE_HEIGHT - TIMELINE_PADDING}
                stroke={line.major ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 255, 255, 0.1)"}
                strokeWidth={line.major ? "1.5" : "1"}
              />
              <text
                x={line.x} y={TIMELINE_HEIGHT - TIMELINE_PADDING + 15}
                fill={line.major ? "rgba(255, 255, 255, 0.8)" : "rgba(255, 255, 255, 0.6)"}
                fontSize={line.major ? "13" : "11"} fontWeight={line.major ? "500" : "400"} textAnchor="middle"
              >
                {line.label}
              </text>
            </g>
          ))}
        </svg>

        {/* Brainwave Regions (dim when volume layer active) */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: activeLayer === 'volume' ? 0.3 : 1 }}>
          {Object.entries(brainwaveRanges).map(([key, range]) => {
            const isActive = currentBrainwave.type === key;
            return (
              <rect
                key={key}
                x={TIMELINE_PADDING} y={frequencyToY(range.max)}
                width={`calc(100% - ${TIMELINE_PADDING * 2}px)`}
                height={frequencyToY(range.min) - frequencyToY(range.max)}
                fill={range.color} opacity={isActive ? 0.3 : 0.1}
                className="transition-opacity duration-300"
              />
            );
          })}
        </svg>

        {/* Frequency Curve Path (always visible, dimmed in volume mode) */}
        {curvePath && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: activeLayer === 'volume' ? 0.3 : 1 }}>
            <defs>
              <linearGradient id="curveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                {nodes.map((node, index) => {
                  const progress = duration > 0 ? (node.time / duration) * 100 : 0;
                  const color = getBrainwaveColor(node.frequency);
                  return (
                    <stop key={index} offset={`${isNaN(progress) ? 0 : progress}%`} stopColor={color} stopOpacity={0.8} />
                  );
                })}
              </linearGradient>
            </defs>
            <path d={curvePath} stroke="url(#curveGradient)" strokeWidth="3" fill="none" className="drop-shadow-lg" />
          </svg>
        )}

        {/* Volume Curve Path */}
        {showVolumeLayer && volumeCurvePath && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: activeLayer === 'frequency' ? 0.4 : 1 }}>
            <path
              d={volumeCurvePath}
              stroke="#F59E0B"
              strokeWidth={activeLayer === 'volume' ? 2.5 : 1.5}
              strokeDasharray={activeLayer === 'volume' ? 'none' : '6 3'}
              fill="none"
              className="drop-shadow-lg"
            />
          </svg>
        )}

        {/* Current Time Indicator */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-red-500 cursor-grab active:cursor-grabbing z-10 timeline-indicator touch-none"
          style={{ left: timeToX(currentTime) }}
          onMouseDown={handleTimelineIndicatorMouseDown}
          onTouchStart={handleTimelineIndicatorTouchStart}
        >
          <div 
            className="absolute -top-3 -left-3 w-6 h-6 bg-red-500 rounded-full shadow-lg cursor-grab active:cursor-grabbing hover:bg-red-400 transition-colors touch-none"
            onMouseDown={handleTimelineIndicatorMouseDown}
            onTouchStart={handleTimelineIndicatorTouchStart}
          />
        </div>

        {/* Frequency Nodes (dim in volume mode) */}
        {nodes.map((node) => (
          <div
            key={node.id}
            className="absolute group cursor-move touch-none"
            style={{
              left: timeToX(node.time) - NODE_SIZE / 2,
              top: frequencyToY(node.frequency) - NODE_SIZE / 2,
              width: NODE_SIZE,
              height: NODE_SIZE,
              opacity: activeLayer === 'volume' ? 0.3 : 1,
              pointerEvents: activeLayer === 'volume' ? 'none' : 'auto',
            }}
            onMouseDown={(e) => handleMouseDown(e, node.id)}
            onTouchStart={(e) => handleTouchStart(e, node.id)}
          >
            <div
              className="w-full h-full rounded-full border-2 border-white shadow-lg transition-all duration-300"
              style={{ 
                backgroundColor: getBrainwaveColor(node.frequency),
                boxShadow: `0 0 15px ${getBrainwaveColor(node.frequency)}80`
              }}
            />
            
            {/* Node Info Tooltip */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-black/90 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap border"
                 style={{ borderColor: getBrainwaveColor(node.frequency) }}>
              <div className="font-medium" style={{ color: getBrainwaveColor(node.frequency) }}>
                {getBrainwaveType(node.frequency).name}
              </div>
              <div>
                {node.frequency.toFixed(1)} Hz @ {
                  node.time >= 3600 
                    ? `${Math.floor(node.time / 3600)}h ${Math.floor((node.time % 3600) / 60)}m ${Math.floor(node.time % 60)}s`
                    : `${Math.floor(node.time / 60)}:${(node.time % 60).toFixed(0).padStart(2, '0')}`
                }
              </div>
            </div>
            
            {/* Delete Button — only rendered on devices with real hover
                (laptops, desktops). Touch-only devices get long-press
                instead. Device-capability gating (not viewport size) so a
                MacBook in split-screen still gets the ×, and a touchscreen
                phone in landscape doesn't. */}
            {hasFineHover && (
              <button
                className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs hover:bg-red-600 active:bg-red-700 z-20"
                onMouseDown={(e) => {
                  // Keep mousedown from triggering a drag on desktop.
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isDragging && !hasActuallyDragged) deleteNode(node.id);
                }}
                aria-label={`Delete node at ${node.frequency.toFixed(1)} Hz`}
                data-testid={`delete-node-${node.id}`}
              >
                ×
              </button>
            )}
          </div>
        ))}

        {/* Volume Nodes */}
        {showVolumeLayer && volumeNodes.map((node) => (
          <div
            key={node.id}
            className="absolute group cursor-move touch-none"
            style={{
              left: timeToX(node.time) - VOL_NODE_SIZE / 2,
              top: volumeToY(node.volume) - VOL_NODE_SIZE / 2,
              width: VOL_NODE_SIZE,
              height: VOL_NODE_SIZE,
              opacity: activeLayer === 'frequency' ? 0.4 : 1,
              pointerEvents: activeLayer === 'frequency' ? 'none' : 'auto',
              zIndex: activeLayer === 'volume' ? 15 : 5,
            }}
            onMouseDown={(e) => handleVolumeNodeMouseDown(e, node.id)}
          >
            <div
              className="w-full h-full rounded-sm border-2 border-amber-300 shadow-lg transition-all"
              style={{ 
                backgroundColor: '#F59E0B',
                boxShadow: '0 0 12px rgba(245, 158, 11, 0.6)',
                transform: 'rotate(45deg)',
              }}
            />
            
            {/* Volume Tooltip */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-3 px-2 py-1 bg-black/90 text-amber-300 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap border border-amber-500/50">
              {Math.round(node.volume * 100)}% @ {
                node.time >= 3600 
                  ? `${Math.floor(node.time / 3600)}h ${Math.floor((node.time % 3600) / 60)}m`
                  : `${Math.floor(node.time / 60)}:${(node.time % 60).toFixed(0).padStart(2, '0')}`
              }
            </div>

            {/* Delete button for volume nodes (min 2 required) */}
            {volumeNodes.length > 2 && (
              <button
                className="absolute -top-2 -right-2 w-5 h-5 md:w-4 md:h-4 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 md:opacity-0 transition-opacity flex items-center justify-center text-xs hover:bg-red-600 z-20"
                style={{ opacity: window.matchMedia('(hover: none)').matches ? '1' : undefined }}
                onTouchStart={(e) => { e.stopPropagation(); }}
                onMouseDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isDragging && !hasActuallyDragged) deleteVolumeNode(node.id);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  deleteVolumeNode(node.id);
                }}
                aria-label={`Delete volume node at ${Math.round(node.volume * 100)}%`}
                data-testid={`delete-volume-node-${node.id}`}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Node List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-32 overflow-y-auto">
        {activeLayer === 'frequency' ? nodes.map((node) => {
          const brainwaveType = getBrainwaveType(node.frequency);
          return (
            <div
              key={node.id}
              className={`flex items-center gap-2 p-3 rounded-lg text-sm transition-all duration-300 border ${
                currentBrainwave.type === brainwaveType.type 
                  ? 'border-2 scale-105 shadow-lg' 
                  : 'border border-white/20'
              }`}
              style={{ 
                backgroundColor: currentBrainwave.type === brainwaveType.type 
                  ? `${brainwaveType.color}20` 
                  : 'rgba(255,255,255,0.1)',
                borderColor: currentBrainwave.type === brainwaveType.type 
                  ? brainwaveType.color 
                  : 'rgba(255,255,255,0.2)',
                boxShadow: currentBrainwave.type === brainwaveType.type 
                  ? `0 0 15px ${brainwaveType.color}30` 
                  : undefined
              }}
            >
              <div
                className="w-4 h-4 rounded-full border border-white/50 shadow-lg"
                style={{ backgroundColor: brainwaveType.color, boxShadow: `0 0 10px ${brainwaveType.color}60` }}
              />
              <div className="flex-1">
                <div className="text-white font-medium">{brainwaveType.name}</div>
                <div className="text-white/70 text-xs">
                  {node.frequency.toFixed(1)} Hz @ {
                    node.time >= 3600 
                      ? `${Math.floor(node.time / 3600)}h ${Math.floor((node.time % 3600) / 60)}m ${Math.floor(node.time % 60)}s`
                      : `${Math.floor(node.time / 60)}:${(node.time % 60).toFixed(0).padStart(2, '0')}`
                  }
                </div>
              </div>
              <button onClick={() => deleteNode(node.id)} className="text-red-400 hover:text-red-300 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        }) : volumeNodes.map((node) => (
          <div
            key={node.id}
            className="flex items-center gap-2 p-3 rounded-lg text-sm border border-amber-500/30 bg-amber-500/10"
          >
            <div className="w-4 h-4 rounded-sm bg-amber-500 border border-amber-300" style={{ transform: 'rotate(45deg)' }} />
            <div className="flex-1">
              <div className="text-amber-300 font-medium">{Math.round(node.volume * 100)}%</div>
              <div className="text-white/70 text-xs">
                @ {node.time >= 3600 
                  ? `${Math.floor(node.time / 3600)}h ${Math.floor((node.time % 3600) / 60)}m`
                  : `${Math.floor(node.time / 60)}:${(node.time % 60).toFixed(0).padStart(2, '0')}`}
              </div>
            </div>
            {volumeNodes.length > 2 && (
              <button onClick={() => deleteVolumeNode(node.id)} className="text-red-400 hover:text-red-300 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Timeline;
